import { yupResolver } from '@hookform/resolvers/yup';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RestoreIcon from '@mui/icons-material/Restore';
import {
	Button,
	Card,
	Grid,
	Link,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
	usePayment,
	useUpdatePaymentStatusMutation,
} from '../../../api/PaymentsApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import { CurrencyCode } from '../../../types/currencyCode';
import { Payment, PaymentActionStatus } from '../../../types/payments';
import { formatCurrency } from '../../../util/util';
import DisplayCard from '../../DisplayCard';
import DropdownButton, { HiddenActionItem } from '../../DropdownButton';
import Error from '../../Error';
import { FastForm } from '../../fastForm';
import { FORM_NAME } from '../../fastForm/data/constants';
import {
	getDefaultPolicyValues,
	getPaymentForm,
} from '../../fastForm/formBuilders';
import { PaymentFormValues } from '../../fastForm/formBuilders/getPaymentForm';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import ApplyPaymentModal from '../components/ApplyPaymentModal';
import ApplyPositivePaymentModal from '../components/ApplyPositivePaymentModal';
import ActiveLastBreadcrumb from '../components/Breadcrumbs';
import DeclinePaymentModal from '../components/DeclinePaymentModal';
import DiscardPaymentModal from '../components/DiscardPaymentModal';
import MarkAsReadyPaymentModal from '../components/MarkAsReadyPaymentModal';
import PaymentStateChip from '../components/PaymentStateChip';
import ReturnPaymentModal from '../components/ReturnPaymentModal';
import ReturnToDraftPaymentModal from '../components/ReturnToDraftPaymentModal';
import {
	DECLINE_PAYMENT_MODAL__BUTTON,
	DISCARD_PAYMENT_MODAL__BUTTON,
	PAYMENT_PAGE__APPLY_PAYMENT_BUTTON,
	PAYMENT_PAGE__EDIT_BUTTON,
	PAYMENT_PAGE__PAYMENT_AMOUNT_TITLE,
	PAYMENT_PAGE__PAYMENT_CREATED_DATE_TITLE,
	PAYMENT_PAGE__PAYMENT_MODIFIED_DATE_TITLE,
	PAYMENT_PAGE__PAYMENT_NOT_FOUND,
	PAYMENT_PAGE__PAYMENT_NOT_FOUND_ACTION_TEXT,
	PAYMENT_PAGE__PAYMENT_STATUS_TITLE,
	PAYMENT_PAGE__POLICYHOLDER_TITLE,
	PAYMENT_PAGE__POLICY_TITLE,
	PAYMENT_PAGE__TITLE,
	READY_TO_POST_MODAL__BUTTON,
	RETURN_PAYMENT_MODAL__BUTTON,
	RETURN_TO_DRAFT_PAYMENT_MODAL__BUTTON,
} from '../data/constants';
import { paymentSchema } from '../lib/paymentSchema';

interface PaymentPageProps {
	payment: Payment;
	disableBreadcrumb: boolean;
}

const PaymentPage = (props: PaymentPageProps) => {
	const authData = useAuthContext();
	const config = useConfigContext();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const policyData = usePolicyContext();
	const [isDiscardPaymentModalOpen, setIsDiscardPaymentModalOpen] =
		useState(false);
	const [isDeclinePaymentModalOpen, setIsDeclinePaymentModalOpen] =
		useState(false);
	const [isReturnPaymentModalOpen, setIsReturnPaymentModalOpen] =
		useState(false);
	const [isReturnToDraftPaymentModalOpen, setIsReturnToDraftPaymentModalOpen] =
		useState(false);
	const [isMarkAsReadyPaymentModalOpen, setIsMarkAsReadyPaymentModalOpen] =
		useState(false);
	const { hasPermission } = authData;
	const timezone = authData.auth?.timezone;
	const authToken = authData.auth?.authorizationToken;
	const { payment, disableBreadcrumb } = props;
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const amount = payment?.amount;
	const policyLocator = payment?.policyLocator;
	const policyholderLocator = payment?.policyholderLocator;
	const createdTimestamp = payment?.createdTimestamp;
	const updatedTimestamp = payment?.updatedTimestamp;

	const displayId = payment?.displayId;
	const state = payment?.state;
	const locator = payment?.locator;

	const paymentFields = config.paymentConfiguration.paymentConfiguration.fields;

	const form = getPaymentForm(
		{
			payment: payment,
			fields: paymentFields,
			fieldValues: payment?.fieldValues || {},
			mediaByLocator: {},
			timezone,
			multiCurrencyEnabled,
		},
		FORM_NAME.payment,
	);

	const paymentFormValues = getDefaultPolicyValues(form, authData);

	const specialFieldValues = {
		amountCurrency: payment?.amountCurrency || '',
		amount: payment?.amount || '',
		policyholderLocator: payment?.policyholderLocator || '',
		policyLocator: payment?.policyLocator || '',
	};

	const defaultValues = {
		payment: {
			...(paymentFormValues.payment as object),
			...specialFieldValues,
		},
	} as PaymentFormValues;

	const methods = useForm<PaymentFormValues>({
		defaultValues,
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			yupResolver(
				getValidationSchemaWithoutResolver(form).concat(paymentSchema(true)),
			),
	});

	const { formState } = methods;
	const { isValid } = formState;

	const formattedModifiedTimestamp =
		authData.getZonedFormattedDateString(updatedTimestamp);

	const formattedCreatedTimestamp =
		authData.getZonedFormattedDateString(createdTimestamp);
	const formattedAmount = formatCurrency(
		parseFloat(amount),
		payment?.amountCurrency as CurrencyCode,
	);

	const [openApplyPayment, setOpenApplyPayment] = useState(false);
	const updatePaymentStatus = useUpdatePaymentStatusMutation({
		paymentLocator: locator,
		authToken,
		queryClient,
	});

	const handleUpdatePaymentStatus = (reason: PaymentActionStatus) => {
		updatePaymentStatus.mutate({
			action: reason,
		});
	};

	const hiddenActions: HiddenActionItem[] = [];

	const discardAction = {
		label: DISCARD_PAYMENT_MODAL__BUTTON,
		callback: () => setIsDiscardPaymentModalOpen(true),
		icon: <DeleteIcon />,
	};

	const returnedAction = {
		label: RETURN_PAYMENT_MODAL__BUTTON,
		callback: () => setIsReturnPaymentModalOpen(true),
		icon: <RestoreIcon />,
	};

	const declinedAction = {
		label: DECLINE_PAYMENT_MODAL__BUTTON,
		callback: () => setIsDeclinePaymentModalOpen(true),
		icon: <HighlightOffIcon />,
	};

	const editAction = {
		label: PAYMENT_PAGE__EDIT_BUTTON,
		callback: () => navigate('edit'),
		icon: <EditIcon />,
	};

	const readyAction = {
		label: READY_TO_POST_MODAL__BUTTON,
		callback: () => setIsMarkAsReadyPaymentModalOpen(true),
		icon: <CheckCircleOutlineIcon />,
	};

	const returnToDraftAction = {
		label: RETURN_TO_DRAFT_PAYMENT_MODAL__BUTTON,
		callback: () => setIsReturnToDraftPaymentModalOpen(true),
		icon: <EditIcon />,
	};

	if (state === 'draft' && hasPermission('payment.update')) {
		hiddenActions.push(editAction);
		if (isValid) {
			hiddenActions.push(readyAction);
		}
		hiddenActions.push(discardAction);
	}
	if (
		(state === 'declined' || state === 'returned') &&
		hasPermission('payment.update')
	) {
		hiddenActions.push(returnToDraftAction);
	}

	if (state === 'ready' && hasPermission('payment.update')) {
		hiddenActions.push(returnToDraftAction);
		hiddenActions.push(returnedAction);
		hiddenActions.push(declinedAction);
	}

	hiddenActions.push();
	hiddenActions.push();

	const renderApplyPaymentModal = () => {
		if (parseFloat(payment.amount) < 0 || !cashManagementEnabled) {
			return (
				<ApplyPaymentModal
					open={openApplyPayment}
					setOpen={(val: boolean) => {
						setOpenApplyPayment(val);
					}}
					payment={payment}
				/>
			);
		}
		return (
			<ApplyPositivePaymentModal
				open={openApplyPayment}
				setOpen={(val: boolean) => {
					setOpenApplyPayment(val);
				}}
				payment={payment}
			/>
		);
	};

	return (
		<FormProvider {...methods}>
			<Stack spacing={2} alignItems='flex-start'>
				{!disableBreadcrumb && <ActiveLastBreadcrumb />}
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='center'
					sx={{ width: '100%' }}
				>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Typography variant='h3'>
							{PAYMENT_PAGE__TITLE} {displayId}
						</Typography>
						{state === 'ready' && authData?.hasPermission('invoice.pay') && (
							<Button
								variant='contained'
								startIcon={<AttachMoneyIcon />}
								onClick={() => {
									setOpenApplyPayment(true);
								}}
							>
								{PAYMENT_PAGE__APPLY_PAYMENT_BUTTON}
							</Button>
						)}
					</Stack>
					<DropdownButton actions={hiddenActions} />
				</Stack>
				<Stack direction='row' spacing={2} alignItems='center'>
					{payment && openApplyPayment && renderApplyPaymentModal()}
					<DisplayCard
						title={PAYMENT_PAGE__PAYMENT_AMOUNT_TITLE}
						value={formattedAmount}
					/>
					<DisplayCard
						title={PAYMENT_PAGE__POLICYHOLDER_TITLE}
						value={policyholderLocator}
					/>
					<DisplayCard
						title={PAYMENT_PAGE__POLICY_TITLE}
						value={
							<Link component={RouterLink} to={`/policy/${policyLocator}`}>
								{policyLocator || '-'}
							</Link>
						}
					/>
					<DisplayCard
						title={PAYMENT_PAGE__PAYMENT_CREATED_DATE_TITLE}
						value={formattedCreatedTimestamp}
					/>
					<DisplayCard
						title={PAYMENT_PAGE__PAYMENT_MODIFIED_DATE_TITLE}
						value={formattedModifiedTimestamp}
					/>
					<DisplayCard
						title={PAYMENT_PAGE__PAYMENT_STATUS_TITLE}
						value={<PaymentStateChip state={state} />}
					/>
				</Stack>
				<Card sx={{ width: '100%', p: 0 }}>
					<Grid container spacing={2} sx={{ p: 2 }}>
						<FastForm
							formName={FORM_NAME.payment}
							form={form}
							productName={policyData?.productConfig?.name}
							configVersion=''
							authData={authData}
							disabled
						/>
					</Grid>
				</Card>
			</Stack>
			<DiscardPaymentModal
				open={isDiscardPaymentModalOpen}
				onClose={() => setIsDiscardPaymentModalOpen(false)}
				onConfirm={() => {
					handleUpdatePaymentStatus('discard');
					setIsDiscardPaymentModalOpen(false);
				}}
			/>
			<ReturnPaymentModal
				open={isReturnPaymentModalOpen}
				onClose={() => setIsReturnPaymentModalOpen(false)}
				onConfirm={() => {
					handleUpdatePaymentStatus('return');
					setIsReturnPaymentModalOpen(false);
				}}
			/>
			<DeclinePaymentModal
				open={isDeclinePaymentModalOpen}
				onClose={() => setIsDeclinePaymentModalOpen(false)}
				onConfirm={() => {
					handleUpdatePaymentStatus('decline');
					setIsDeclinePaymentModalOpen(false);
				}}
			/>
			<ReturnToDraftPaymentModal
				open={isReturnToDraftPaymentModalOpen}
				onClose={() => setIsReturnToDraftPaymentModalOpen(false)}
				onConfirm={() => {
					handleUpdatePaymentStatus('draft');
					setIsReturnToDraftPaymentModalOpen(false);
					navigate('edit');
				}}
			/>
			<MarkAsReadyPaymentModal
				open={isMarkAsReadyPaymentModalOpen}
				onClose={() => setIsMarkAsReadyPaymentModalOpen(false)}
				onConfirm={() => {
					handleUpdatePaymentStatus('ready');
					setIsMarkAsReadyPaymentModalOpen(false);
				}}
			/>
		</FormProvider>
	);
};

const PaymentPageLoadingSkeleton = () => {
	return (
		<Stack spacing={2} alignItems='flex-start'>
			<ActiveLastBreadcrumb loading />
			<Stack direction='row' spacing={2} alignItems='center'>
				<Typography variant='h3'>
					<Skeleton variant='text' width={80} />
				</Typography>
			</Stack>
			<Stack direction='row' spacing={2} alignItems='center'>
				<DisplayCard title={PAYMENT_PAGE__PAYMENT_AMOUNT_TITLE} loading />
				<DisplayCard title={PAYMENT_PAGE__POLICYHOLDER_TITLE} loading />
				<DisplayCard title={PAYMENT_PAGE__POLICY_TITLE} loading />
				<DisplayCard title={PAYMENT_PAGE__PAYMENT_CREATED_DATE_TITLE} loading />
				<DisplayCard title={PAYMENT_PAGE__PAYMENT_STATUS_TITLE} loading />
			</Stack>
			<Card sx={{ width: '100%', height: 96 }}></Card>
		</Stack>
	);
};

const PaymentPageWrapper = () => {
	const { paymentLocator, policyholderId, policyId } = useParams();
	const navigate = useNavigate();
	const authData = useAuthContext();
	const authToken = authData.auth?.authorizationToken;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const {
		data: payment,
		isLoading,
		isError,
		error,
	} = usePayment(
		paymentLocator,
		authToken,
		cashManagementEnabled,
		authData.hasPermission('payment.fetch'),
	);

	if (isError && axios.isAxiosError(error)) {
		return (
			<Error
				error={
					error?.response?.data.message ||
					`${PAYMENT_PAGE__PAYMENT_NOT_FOUND} ${paymentLocator}`
				}
				primaryAction={() => navigate('/payments')}
				primaryActionText={PAYMENT_PAGE__PAYMENT_NOT_FOUND_ACTION_TEXT}
			/>
		);
	}

	if (isLoading) {
		return <PaymentPageLoadingSkeleton />;
	}

	return (
		<PaymentPage
			payment={payment}
			disableBreadcrumb={!!policyholderId || !!policyId}
		/>
	);
};

export default PaymentPageWrapper;
